import { useQuery } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useState } from 'react'
import { useParams } from 'react-router'

import { Button } from '@/components/elements/button'
import { Skeleton } from '@/components/elements/skeleton'
import { Layout } from '@/components/Layout'
import { getEntityJobs, getEntityRoleProfile } from '@/services/entity.service'
import { UserStoreContext } from '@/stores/UserStore'
import { ENTITY_ROLES } from '@/utils/constants'
import { formatUserName } from '@/utils/userHelpers'

import AddEditUserSlideout from '../AddEditUserSlideout'

import { GeneralSection, InstructorSection, StudentSection } from './Sections'

const Profile = observer(() => {
  const { entityId, roleId } = useParams()
  const {
    user: viewer,
    canManageEntity,
    canManageInstructorResources,
  } = useContext(UserStoreContext)
  const [isEditing, setIsEditing] = useState(false)

  const queryKey = ['accounts', 'users', 'profile', entityId, roleId]
  const {
    data: entityRole,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => getEntityRoleProfile({ entityId, roleId }),
    enabled: !!entityId && !!roleId,
    retry: false,
  })

  // Jobs are needed to drive the edit slideout's job picker — only fetch when
  // the admin actually opens the slideout, not on initial profile render.
  const { data: jobsData } = useQuery({
    queryKey: ['accounts', 'jobs', entityId],
    queryFn: () => getEntityJobs({ entityId }),
    enabled: canManageEntity && !!entityId && isEditing,
  })

  // Breadcrumbs: only link back to the admin Users list when the viewer can
  // actually access it. Non-admin viewers (Instructor/Member) get a plain
  // "Profile" trail instead so clicking the breadcrumb doesn't drop them on
  // a Forbidden page.
  const userName = entityRole ? formatUserName(entityRole.user) : 'Profile'
  const breadcrumbs = canManageEntity
    ? [{ pathname: `/accounts/${entityId}/users`, title: 'Users' }, userName]
    : [userName]

  if (isError) {
    return (
      <Layout breadcrumbs={breadcrumbs}>
        <Layout.Heading title="Profile" />
        <div className="border-destructive text-destructive mt-6 rounded-lg border-1 bg-red-50 p-4 text-sm">
          {error?.message || 'Unable to load this profile.'}
        </div>
      </Layout>
    )
  }

  if (isPending || !entityRole) {
    return (
      <Layout breadcrumbs={breadcrumbs}>
        <Layout.Heading title="Profile" />
        <div className="mt-6 flex flex-col gap-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Layout>
    )
  }

  const { user } = entityRole
  const isSelf = viewer?.id === user.id
  const isInstructorInEntity = entityRole.roles?.includes(ENTITY_ROLES.INSTRUCTOR)
  const isMemberInEntity = entityRole.roles?.includes(ENTITY_ROLES.MEMBER)

  const showInstructorSection =
    isInstructorInEntity ||
    user.isXverseInstructor ||
    user.isXverseInstructorCandidate ||
    !!user.highestTierInstructorNumber
  const showStudentSection = isMemberInEntity || (entityRole.studentCertificates?.length || 0) > 0

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Layout.Heading title={formatUserName(user)}>
        {canManageEntity && (
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            <Pencil className="mr-1 h-4 w-4" />
            Edit
          </Button>
        )}
      </Layout.Heading>

      <div className="mt-6 flex flex-col gap-6 pb-12">
        <GeneralSection entityRole={entityRole} isSelf={isSelf} />
        {showInstructorSection && <InstructorSection entityRole={entityRole} />}
        {showStudentSection && (
          <StudentSection
            entityRole={entityRole}
            entityId={entityId}
            canManage={canManageInstructorResources}
          />
        )}
      </div>

      {isEditing && (
        <AddEditUserSlideout
          editUser={entityRole}
          entityId={entityId}
          jobs={jobsData?.results || []}
          queryKey={queryKey}
          resetAndClose={() => setIsEditing(false)}
        />
      )}
    </Layout>
  )
})

export default Profile
